import React, { useDeferredValue, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { sitemap } from "./sitemapData";
import "../styles/SiteMapQuick.css";

const sectionThemes = {
  "Utah Verticals": {
    accent: "#0f766e",
    soft: "#ccfbf1",
    glow: "rgba(15, 118, 110, 0.18)",
  },
  "Utah At a Glance": {
    accent: "#2563eb",
    soft: "#dbeafe",
    glow: "rgba(37, 99, 235, 0.18)",
  },
  "Other Pages": {
    accent: "#ea580c",
    soft: "#ffedd5",
    glow: "rgba(234, 88, 12, 0.18)",
  },
};

const getSectionTheme = (label) =>
  sectionThemes[label] || {
    accent: "#0891b2",
    soft: "#cffafe",
    glow: "rgba(8, 145, 178, 0.18)",
  };

const filterNode = (node, query) => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return node;
  }

  const matchingChildren = (node.children || [])
    .map((child) => filterNode(child, normalizedQuery))
    .filter(Boolean);

  const selfMatches =
    node.label.toLowerCase().includes(normalizedQuery) ||
    (node.path || "").toLowerCase().includes(normalizedQuery);

  if (selfMatches || matchingChildren.length) {
    return {
      ...node,
      children: matchingChildren,
    };
  }

  return null;
};

const buildVisibleTree = (rootNode, activeSection, query) => {
  const scopedRoot =
    activeSection === "all"
      ? rootNode
      : {
          ...rootNode,
          children: (rootNode.children || []).filter(
            (section) => section.label === activeSection,
          ),
        };

  return filterNode(scopedRoot, query) || {
    ...scopedRoot,
    children: [],
  };
};

const countLinks = (nodes = []) =>
  nodes.reduce((total, node) => total + 1 + countLinks(node.children || []), 0);

const QuickBranch = ({ node, depth = 0 }) => {
  const hasChildren = Boolean(node.children?.length);

  if (!hasChildren) {
    return node.path ? (
      <Link
        to={node.path}
        className={`sitemap-quick-link depth-${Math.min(depth, 3)}`}
      >
        <span>{node.label}</span>
        <ArrowUpRight size={12} />
      </Link>
    ) : (
      <div className={`sitemap-quick-link passive depth-${Math.min(depth, 3)}`}>
        <span>{node.label}</span>
      </div>
    );
  }

  return (
    <article className={`sitemap-quick-group depth-${Math.min(depth, 3)}`}>
      <div className="sitemap-quick-groupHeader">
        <div>
          <h3>{node.label}</h3>
          {node.path ? <p>{node.path}</p> : null}
        </div>
        {node.path ? (
          <Link
            to={node.path}
            className="sitemap-quick-groupOpen"
            aria-label={`Open ${node.label}`}
          >
            <ArrowUpRight size={13} />
          </Link>
        ) : null}
      </div>

      <div className="sitemap-quick-groupChildren">
        {node.children.map((child) => (
          <QuickBranch
            key={`${child.label}-${child.path || "group"}`}
            node={child}
            depth={depth + 1}
          />
        ))}
      </div>
    </article>
  );
};

function SiteMapQuick() {
  const rootNode = sitemap[0];
  const availableSections = rootNode.children || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSection] = useState("all");
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const visibleTree = useMemo(
    () => buildVisibleTree(rootNode, activeSection, deferredSearchTerm),
    [activeSection, deferredSearchTerm, rootNode],
  );

  const visibleSections = visibleTree.children || [];
  const visibleCount = useMemo(
    () => countLinks(visibleSections),
    [visibleSections],
  );

  return (
    <div className="sitemap-quick-page">
      <Header />

      <main className="sitemap-quick-main">
        <section className="sitemap-quick-shell">
          <div className="sitemap-quick-topbar">
            <div className="sitemap-quick-intro">
              <span className="sitemap-quick-kicker">Site Navigation</span>
              <h1>Browse all pages in one place.</h1>
            </div>

            <div className="sitemap-quick-meta">
              <strong>{visibleCount}</strong>
              <span>Visible links</span>
            </div>
          </div>

          <div className="sitemap-quick-toolbar">
            <label className="sitemap-quick-search">
              <Search size={16} />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search links"
                aria-label="Search sitemap quick links"
              />
            </label>

            <div className="sitemap-quick-filters">
              <button
                type="button"
                className={activeSection === "all" ? "active" : ""}
                onClick={() => setActiveSection("all")}
              >
                All
              </button>
              {availableSections.map((section) => (
                <button
                  key={section.label}
                  type="button"
                  className={activeSection === section.label ? "active" : ""}
                  onClick={() => setActiveSection(section.label)}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sitemap-quick-board">
            {visibleSections.length ? (
              visibleSections.map((section) => {
                const theme = getSectionTheme(section.label);

                return (
                  <section
                    key={`${section.label}-${section.path || "group"}`}
                    className="sitemap-quick-panel"
                    style={{
                      "--section-accent": theme.accent,
                      "--section-soft": theme.soft,
                      "--section-glow": theme.glow,
                    }}
                  >
                    <div className="sitemap-quick-panelHeader">
                      <div>
                        <span className="sitemap-quick-panelTag">Section</span>
                        <h2>{section.label}</h2>
                      </div>
                      {section.path ? (
                        <Link
                          to={section.path}
                          className="sitemap-quick-panelOpen"
                        >
                          <ArrowUpRight size={14} />
                        </Link>
                      ) : null}
                    </div>

                    <div className="sitemap-quick-panelBody">
                      {(section.children || []).map((node) => (
                        <QuickBranch
                          key={`${node.label}-${node.path || "group"}`}
                          node={node}
                        />
                      ))}
                    </div>
                  </section>
                );
              })
            ) : (
              <div className="sitemap-quick-empty">
                <h2>No matching pages</h2>
                <p>Try a shorter search term or view all sections.</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default SiteMapQuick;
